import { Controller } from "@hotwired/stimulus";

/**
 * カート追加モーダルの数量操作と価格計算を管理するStimulusコントローラー
 *
 * 使用場所:
 * - カート追加モーダル内の数量選択部分のみ
 *
 * 主な機能:
 * - 商品数量の増減操作（+/-ボタン）
 * - 数量制限チェック（最大値制限）
 * - 日別・全体の価格計算とリアルタイム更新
 * - エラーメッセージの表示制御
 * - 数量に応じた視覚的スタイル変更（0の場合はグレー、1以上は緑系）
 *
 * cart_confirm_controllerとの違い:
 * - cart_controller: カート追加モーダル専用（このファイル）
 * - cart_confirm_controller: カート確認ドロワー専用
 *
 * 必要なターゲット要素:
 * - value: 数量表示エリア
 * - minus/plus: 数量増減ボタン
 * - input: 数量入力フィールド
 * - price: 価格表示エリア
 * - container: コンテナ要素
 * - dateLabel: 日付ラベル
 * - counter: カウンター表示
 * - totalPrice: 合計価格表示
 * - dayTotal: 日別合計表示
 * - errorMessage: エラーメッセージ表示エリア
 */
export default class extends Controller {
  static targets = [
    "value",
    "minus",
    "plus",
    "input",
    "price",
    "container",
    "dateLabel",
    "counter",
    "totalPrice",
    "dayTotal",
    "errorMessage",
  ];
  static values = {
    limit: Number,
    date: String,
    unitPrice: Number,
  };

  connect() {
    this.count = parseInt(this.inputTarget.value, 10) || 0;
    // 単価を取得（priceTargetがない場合はvalueから取得）
    if (this.hasPriceTarget) {
      this.unitPrice =
        parseInt(this.priceTarget.dataset.cartUnitPrice, 10) || 0;
    } else {
      this.unitPrice = this.unitPriceValue || 0;
    }
    // バリデーション状態を初期化
    this.isValid = true;
    this.update();
  }

  increment() {
    if (this.count < this.limitValue) {
      this.count++;
      this.update();
    }
  }

  decrement() {
    if (this.count > 0) {
      this.count--;
      this.update();
    }
  }

  update() {
    this.valueTarget.value = this.count;
    this.minusTarget.disabled = this.count === 0;
    this.plusTarget.disabled = this.count >= this.limitValue;
    this.inputTarget.value = this.count;

    // hidden_fieldも更新（フォーム送信用）
    const hiddenInput = this.element.querySelector('input[name*="[quantity]"]');
    if (hiddenInput) {
      hiddenInput.value = this.count;
    }

    // 価格を更新
    if (this.hasPriceTarget) {
      const totalPrice = this.count * this.unitPrice;
      this.priceTarget.textContent = totalPrice.toLocaleString() + " 円";
    }

    // デザインを動的に変更
    this.updateStyles();

    // 合計金額を更新
    this.updateTotalPrice();
  }

  updateStyles() {
    const hasItems = this.count > 0;

    // コンテナの背景色
    if (this.hasContainerTarget) {
      if (hasItems) {
        this.containerTarget.classList.add("bg-green-50");
      } else {
        this.containerTarget.classList.remove("bg-green-50");
      }
    }

    // 日付ラベルの色
    if (this.hasDateLabelTarget) {
      if (hasItems) {
        this.dateLabelTarget.classList.remove("text-foreground");
        this.dateLabelTarget.classList.add("text-green-700");
      } else {
        this.dateLabelTarget.classList.remove("text-green-700");
        this.dateLabelTarget.classList.add("text-foreground");
      }
    }

    // 価格の色
    if (this.hasPriceTarget) {
      if (hasItems) {
        this.priceTarget.classList.remove("text-foreground");
        this.priceTarget.classList.add("text-green-700");
      } else {
        this.priceTarget.classList.remove("text-green-700");
        this.priceTarget.classList.add("text-foreground");
      }
    }

    // カウンターの背景色と文字色
    if (this.hasCounterTarget) {
      if (hasItems) {
        this.counterTarget.classList.remove("bg-muted");
        this.counterTarget.classList.add("bg-green-100");
      } else {
        this.counterTarget.classList.remove("bg-green-100");
        this.counterTarget.classList.add("bg-muted");
      }
    }

    // 値の文字色
    if (this.hasValueTarget) {
      if (hasItems) {
        this.valueTarget.classList.remove("text-foreground");
        this.valueTarget.classList.add("text-emerald-700");
      } else {
        this.valueTarget.classList.remove("text-emerald-700");
        this.valueTarget.classList.add("text-foreground");
      }
    }

    // ボタンの枠線色
    [this.minusTarget, this.plusTarget].forEach((button) => {
      if (hasItems) {
        button.classList.remove("outline-border");
        button.classList.add("outline-green-700");
      } else {
        button.classList.remove("outline-green-700");
        button.classList.add("outline-border");
      }
    });
  }

  updateTotalPrice() {
    // カート追加モーダルの合計金額更新
    const modal = document.querySelector("#add-to-cart-modal-content");
    if (modal) {
      const totalPriceElement = modal.querySelector(
        '[data-cart-target="totalPrice"]'
      );
      if (totalPriceElement) {
        const allInputs = modal.querySelectorAll('[data-cart-target="input"]');
        let totalQuantity = 0;

        allInputs.forEach((input) => {
          totalQuantity += parseInt(input.value, 10) || 0;
        });

        const totalAmount = totalQuantity * this.unitPrice;
        totalPriceElement.innerHTML = `<span class="text-foreground text-lg font-bold leading-7">${totalAmount.toLocaleString()} </span><span class="text-foreground text-base font-medium leading-normal">円</span>`;
      }
    }
  }

  // 直接入力時のバリデーション
  validateInput() {
    const inputValue = parseInt(this.valueTarget.value, 10) || 0;
    this.count = inputValue;

    // バリデーション
    if (inputValue > this.limitValue) {
      this.showError();
      this.isValid = false;
    } else {
      this.hideError();
      this.isValid = true;
    }

    this.update();
  }

  // エラーメッセージ表示
  showError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = `${this.limitValue}個まで注文いただけます`;
      this.errorMessageTarget.classList.remove("hidden");
    }
  }

  // エラーメッセージ非表示
  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add("hidden");
    }
  }

  // 日付フォーマット
  formatDate() {
    const date = new Date(this.dateValue);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}月${day}日`;
  }
}
